/**
 * Unified API Service Implementation
 * Provides a consistent interface for all asset types with failover and resilience
 */

import {
  ApiService,
  ApiProvider,
  Asset,
  AssetPrice,
  AssetType,
  TimeRange,
  HistoricalData,
  AssetSearchResult,
  AssetValidationResult,
  ApiKeyTestResult,
  RequestConfig,
  RequestStats,
  ApiError,
  ApiErrorCode
} from '@/types/api';

import { errorHandler, createApiError, categorizeHttpError, extractRetryAfter } from './errors';
import { apiCache, CacheKeyGenerator, CacheConfig } from './cache';
import { providerRegistry } from './providerRegistry';
import { assetDataService } from './assetDataService';
import { assetDictionaryCache } from './assetDictionaryCache';

/**
 * Request deduplication manager
 */
class RequestDeduplicator {
  private pendingRequests = new Map<string, Promise<any>>();
  
  async deduplicate<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    // Check if request is already pending
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key) as Promise<T>;
    }
    
    // Create new request
    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key);
    });
    
    this.pendingRequests.set(key, promise);
    return promise;
  }
  
  clear(): void {
    this.pendingRequests.clear();
  }
}

/**
 * Rate limiter implementation
 */
class RateLimiter {
  private requests = new Map<string, number[]>();
  private limits = new Map<string, { requests: number; window: number }>();
  
  constructor() {
    // Default rate limits (requests per minute)
    this.limits.set('cryptowatchr', { requests: 60, window: 60000 });
    this.limits.set('twelvedata', { requests: 8, window: 60000 });
  }
  
  async checkLimit(provider: string): Promise<boolean> {
    const limit = this.limits.get(provider);
    if (!limit) return true;
    
    const now = Date.now();
    const requests = this.requests.get(provider) || [];
    
    // Remove old requests outside the window
    const validRequests = requests.filter(time => now - time < limit.window);
    
    // Check if we're under the limit
    if (validRequests.length >= limit.requests) {
      return false;
    }
    
    // Add current request
    validRequests.push(now);
    this.requests.set(provider, validRequests);
    
    return true;
  }
  
  setLimit(provider: string, requests: number, windowMs: number): void {
    this.limits.set(provider, { requests, window: windowMs });
  }
}

/**
 * Retry mechanism with exponential backoff
 */
class RetryManager {
  async withRetry<T>(
    operation: () => Promise<T>,
    config: {
      maxRetries?: number;
      baseDelay?: number;
      maxDelay?: number;
      retryCondition?: (error: any) => boolean;
    } = {}
  ): Promise<T> {
    const {
      maxRetries = 3,
      baseDelay = 1000,
      maxDelay = 10000,
      retryCondition = (error) => error.retryable !== false
    } = config;
    
    let lastError: any;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        
        // Don't retry on last attempt or if error is not retryable
        if (attempt === maxRetries || !retryCondition(error)) {
          throw error;
        }
        
        // Calculate delay with exponential backoff
        const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
        
        // Add jitter to prevent thundering herd
        const jitter = Math.random() * 0.1 * delay;
        const totalDelay = delay + jitter;
        
        console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${totalDelay}ms`);
        await new Promise(resolve => setTimeout(resolve, totalDelay));
      }
    }
    
    throw lastError;
  }
}

/**
 * Unified API Service Implementation
 */
export class UnifiedApiService implements ApiService {
  private deduplicator = new RequestDeduplicator();
  private rateLimiter = new RateLimiter();
  private retryManager = new RetryManager();
  private abortControllers = new Map<string, AbortController>();
  private recentLogs: Array<any> = [];
  private requestContexts = new Map<string, {
    startTime: number;
    operation: string;
    provider?: string;
    cancelled: boolean;
  }>();
  private sessionId: string = this.generateSessionId();
  private monitoringCallback?: (event: any) => void;
  
  // Track provider failure backoff state
  private providerNextAvailableTimes = new Map<string, number>();
  private providerFailureCounts = new Map<string, number>();
  private readonly FAILURE_COOLDOWN_MS = 20000; // 20 seconds base
  private readonly FAILURE_COOLDOWN_MAX_MS = 300000; // 5 minutes max
  private readonly FAILURE_JITTER_RATIO = 0.1; // +/-10% jitter
  
  // Track last request time per provider for rate limiting
  private lastRequestTimes = new Map<string, number>();
  private readonly MIN_REQUEST_INTERVAL_MS = 100; // Minimum 100ms between requests to same provider
  
  // Request statistics
  private stats: RequestStats = {
    totalRequests: 0,
    successfulRequests: 0,
    failedRequests: 0,
    averageResponseTime: 0,
    requestsByProvider: {},
    errorsByType: {
      NETWORK_ERROR: 0,
      TIMEOUT_ERROR: 0,
      RATE_LIMIT_ERROR: 0,
      AUTH_ERROR: 0,
      VALIDATION_ERROR: 0,
      NOT_FOUND_ERROR: 0,
      SERVER_ERROR: 0,
      UNKNOWN_ERROR: 0
    },
    lastUpdated: new Date()
  };
  
  constructor() {
    // Provider registry handles initialization
  }
  
  /**
   * Get asset price with failover and caching
   */
  async getAssetPrice(asset: Asset, config?: RequestConfig): Promise<AssetPrice> {
    const cacheKey = CacheKeyGenerator.assetPrice(asset.id);
    
    // Check cache first
    const cached = apiCache.get<AssetPrice>(cacheKey);
    if (cached && !this.isCacheStale(cached, CacheConfig.ASSET_PRICE)) {
      return cached.data;
    }
    
    // Create request context for tracking
    const requestContext = config?.context || this.createRequestContext('getAssetPrice', config?.context?.split('-')[0]);
    
    // Create abort controller for request cancellation
    const abortController = new AbortController();
    this.abortControllers.set(requestContext, abortController);
    
    // Update request context with provider info
    const contextData = this.requestContexts.get(requestContext);
    if (contextData) {
      contextData.provider = 'determining'; // Will be updated when provider is selected
    }
    
    // Log request start
    this.logRequestLifecycle(requestContext, 'start', { asset: asset.symbol, cacheKey });
    
    try {
      // Deduplicate requests
      const result = await this.deduplicator.deduplicate(cacheKey, async () => {
        // Check if request was cancelled
        if (abortController.signal.aborted) {
          this.logRequestLifecycle(requestContext, 'cancelled');
          throw new Error('Request cancelled');
        }
        
        const result = await this.executeWithFailover(
          async (provider) => {
            // Update context with actual provider
            const contextData = this.requestContexts.get(requestContext);
            if (contextData) {
              contextData.provider = provider.name;
            }
            
            await this.checkRateLimit(provider.name);
            
            // Check cancellation before making request
            if (abortController.signal.aborted) {
              this.logRequestLifecycle(requestContext, 'cancelled');
              throw new Error('Request cancelled');
            }
            
            return provider.getAssetPrice(asset, { ...config, signal: abortController.signal });
          },
          asset.type,
          config,
          asset
        );
        
        // Cache the result
        apiCache.set(cacheKey, result, CacheConfig.ASSET_PRICE);
        
        return result;
      });
      
      // Log successful completion
      this.logRequestLifecycle(requestContext, 'success', { result: result.symbol });
      
      return result;
    } catch (error) {
      // Log error
      this.logRequestLifecycle(requestContext, 'error', { 
        error: error instanceof Error ? error.message : String(error) 
      });
      throw error;
    } finally {
      // Clean up abort controller
      this.abortControllers.delete(requestContext);
    }
  }
  
  /**
   * Get multiple asset prices with batching optimization
   */
  async getAssetPrices(assets: Asset[], config?: RequestConfig): Promise<AssetPrice[]> {
    const cacheKey = CacheKeyGenerator.assetPrices(assets.map(a => a.id));
    
    // Check cache first
    const cached = apiCache.get<AssetPrice[]>(cacheKey);
    if (cached && !this.isCacheStale(cached, CacheConfig.ASSET_PRICE)) {
      return cached.data;
    }
    
    // Group assets by type for optimal provider selection
    const assetsByType = this.groupAssetsByType(assets);
    const results: AssetPrice[] = [];
    
    let isFirstType = true;
    for (const [type, typeAssets] of assetsByType.entries()) {
      // Add delay between different asset type requests to limit rate
      if (!isFirstType) {
        await new Promise(resolve => setTimeout(resolve, this.MIN_REQUEST_INTERVAL_MS));
      }
      isFirstType = false;
      
      try {
        const typeResults = await this.deduplicator.deduplicate(
          `prices:${type}:${typeAssets.map(a => a.id).join(',')}`,
          async () => {
            return this.executeWithFailover(
              async (provider) => {
                await this.checkRateLimit(provider.name);
                return provider.getAssetPrices(typeAssets, config);
              },
              type,
              config,
              typeAssets[0] // Pass first asset for exchange detection
            );
          }
        );
        
        results.push(...typeResults);
      } catch (error) {
        // If one asset type fails, continue with other types
        // Only log the error, don't throw - we want to return partial results
        console.warn(`Failed to get prices for ${type} assets:`, error);
        // Continue to next asset type
      }
    }
    
    // Return results even if some asset types failed
    // As long as we have at least one successful result, it's considered a success
    if (results.length > 0) {
      // Cache only when all assets are successfully returned
      if (results.length === assets.length) {
        apiCache.set(cacheKey, results, CacheConfig.ASSET_PRICE);
      }
      return results;
    }
    
    // Only throw error if ALL asset types failed
    throw createApiError('SERVER_ERROR', 'All providers failed to fetch asset prices');
  }
  
  /**
   * Get historical data with caching
   */
  async getHistoricalData(
    asset: Asset,
    timeRange: TimeRange,
    config?: RequestConfig
  ): Promise<HistoricalData> {
    const cacheKey = CacheKeyGenerator.historicalData(asset.id, timeRange, config?.interval);
    
    // Check cache first
    const cached = apiCache.get<HistoricalData>(cacheKey);
    if (cached && !this.isCacheStale(cached, CacheConfig.HISTORICAL_DATA)) {
      return cached.data;
    }
    
    return this.deduplicator.deduplicate(cacheKey, async () => {
      const result = await this.executeWithFailover(
        async (provider) => {
          await this.checkRateLimit(provider.name);
          return provider.getHistoricalData(asset, timeRange, config);
        },
        asset.type,
        config,
        asset
      );
      
      // Cache the result
      apiCache.set(cacheKey, result, CacheConfig.HISTORICAL_DATA);
      
      return result;
    });
  }
  
  /**
   * Search assets with caching
   * Optimized search flow: Built-in assets → Cache → API → Validation
   */
  async searchAssets(
    query: string,
    type?: AssetType,
    config?: RequestConfig
  ): Promise<AssetSearchResult[]> {
    const cacheKey = CacheKeyGenerator.assetSearch(query, type);
    
    // Step 1: Check cache first (API results cache)
    const cached = apiCache.get<AssetSearchResult[]>(cacheKey);
    if (cached && !this.isCacheStale(cached, CacheConfig.SEARCH_RESULTS)) {
      return cached.data;
    }
    
    return this.deduplicator.deduplicate(cacheKey, async () => {
      // Check if request was aborted
      if (config?.signal?.aborted) {
        throw new Error('Request aborted');
      }
      
      const trimmedQuery = query.trim();
      const allResults: AssetSearchResult[] = [];
      const seenSymbols = new Set<string>(); // For deduplication
      
      // Step 2: Search built-in assets (fastest, memory-based)
      // Ensure AssetDataService is initialized
      if (!assetDataService.isInitialized()) {
        await assetDataService.initialize();
      }
      
      // Check abort before continuing
      if (config?.signal?.aborted) {
        throw new Error('Request aborted');
      }
      
      const builtInResults = assetDataService.search(trimmedQuery, type, {
        matchMode: 'contains',
        maxResults: 50,
        relevanceThreshold: 0
      });
      
      // Add built-in results (they have highest priority)
      for (const asset of builtInResults) {
        const symbolKey = `${asset.symbol.toUpperCase()}-${asset.type}`;
        if (!seenSymbols.has(symbolKey)) {
          seenSymbols.add(symbolKey);
          allResults.push(asset);
        }
      }

      // Step 2.5: Add common alias matches (offline fallback)
      if (!type || type === 'stock') {
        const alias = trimmedQuery.toLowerCase();
        const addAlias = (symbol: string, name: string, exchange?: string) => {
          const symbolKey = `${symbol.toUpperCase()}-stock`;
          if (!seenSymbols.has(symbolKey)) {
            seenSymbols.add(symbolKey);
            allResults.push({
              id: `stock-${symbol.toLowerCase()}`,
              symbol,
              name,
              type: 'stock',
              ...(exchange ? { exchange } : {})
            });
          }
        };

        if (alias.startsWith('tenc') || alias.startsWith('tencent')) {
          addAlias('0700.HK', 'Tencent Holdings', 'HK');
        }
        if (alias.startsWith('xiaomi') || alias.startsWith('mi')) {
          addAlias('1810.HK', 'Xiaomi', 'HK');
        }
        if (alias.includes('moutai') || alias.includes('maotai')) {
          addAlias('600519.SH', 'Kweichow Moutai', 'SH');
        }
        if (trimmedQuery.includes('腾讯')) {
          addAlias('0700.HK', '腾讯控股', 'HK');
        }
        if (trimmedQuery.includes('小米')) {
          addAlias('1810.HK', '小米集团', 'HK');
        }
        if (trimmedQuery.includes('茅台')) {
          addAlias('600519.SH', '贵州茅台', 'SH');
        }
      }
      
      // Step 3: If we have good built-in results, return early (optimization)
      // Only skip API search if we have exact or high-relevance matches
      const hasExactMatch = builtInResults.some(asset => 
        asset.symbol.toUpperCase() === trimmedQuery.toUpperCase() ||
        asset.name.toLowerCase() === trimmedQuery.toLowerCase()
      );
      
      // Check abort before API search
      if (config?.signal?.aborted) {
        throw new Error('Request aborted');
      }
      
      // Step 4: Parallel search via API providers and AKTools fallback
      // Execute both searches in parallel for better performance
      let apiResults: AssetSearchResult[] = [];
      let aktoolsResults: AssetSearchResult[] = [];
      
      if (!hasExactMatch || allResults.length < 5) {
        // Helper function to search via API providers
        const searchViaProviders = async (): Promise<AssetSearchResult[]> => {
          try {
            const providers = this.getHealthyProviders(type);
            for (const provider of providers) {
              // Check abort before each provider
              if (config?.signal?.aborted) {
                throw new Error('Request aborted');
              }
              
              // Skip providers in cooldown window
              const nextAvailableTime = this.providerNextAvailableTimes.get(provider.name);
              if (nextAvailableTime && Date.now() < nextAvailableTime) {
                continue;
              }

              try {
                await this.checkRateLimit(provider.name);
                
                // Check abort before making request
                if (config?.signal?.aborted) {
                  throw new Error('Request aborted');
                }
                
                const res = await provider.searchAssets(query, type, config);
                if (Array.isArray(res) && res.length > 0) {
                  return res;
                }
              } catch (err: any) {
                // Ignore abort errors
                if (err instanceof Error && err.name === 'AbortError') {
                  throw err;
                }
                // Respect rate-limit cooldown for this provider when possible
                const isRateLimit = err && typeof err === 'object' && err.code === 'RATE_LIMIT_ERROR';
                const retryAfter = isRateLimit && typeof err.retryAfter === 'number' ? err.retryAfter : undefined;
                if (isRateLimit) {
                  const delay = retryAfter && retryAfter > 0 ? retryAfter : this.FAILURE_COOLDOWN_MS;
                  this.providerNextAvailableTimes.set(provider.name, Date.now() + delay);
                }
                // Try next provider
                continue;
              }
            }
            return [];
          } catch (error) {
            // If API search fails, return empty array
            if (error instanceof Error && error.name === 'AbortError') {
              throw error;
            }
            return [];
          }
        };
        
        // Helper function to search via AKTools fallback
        const searchViaAktools = async (): Promise<AssetSearchResult[]> => {
          if (apiResults.length > 0 || (type && type !== 'stock')) {
            return []; // Skip if we already have results or not stock type
          }
          
          const aktoolsProvider = providerRegistry.getProvider('aktools');
          if (!aktoolsProvider) {
            return [];
          }
          
          try {
            // Check abort before making request
            if (config?.signal?.aborted) {
              throw new Error('Request aborted');
            }
            
            return await aktoolsProvider.searchAssets(query, type, {
              ...config,
              timeout: Math.min(config?.timeout ?? 15000, 15000)
            });
          } catch (error) {
            // Ignore abort errors (will be handled by caller)
            if (error instanceof Error && error.name === 'AbortError') {
              throw error;
            }
            // Ignore other fallback failures
            return [];
          }
        };
        
        // Execute both searches in parallel
        try {
          const [providerSettled, aktoolsSettled] = await Promise.allSettled([
            searchViaProviders(),
            searchViaAktools()
          ]);
          
          // Process provider results
          if (providerSettled.status === 'fulfilled') {
            apiResults = providerSettled.value;
          } else if (providerSettled.reason instanceof Error && providerSettled.reason.name === 'AbortError') {
            throw providerSettled.reason;
          }
          
          // Process AKTools results
          if (aktoolsSettled.status === 'fulfilled') {
            aktoolsResults = aktoolsSettled.value;
          } else if (aktoolsSettled.reason instanceof Error && aktoolsSettled.reason.name === 'AbortError') {
            throw aktoolsSettled.reason;
          }
        } catch (error) {
          // If abort error, rethrow
          if (error instanceof Error && error.name === 'AbortError') {
            throw error;
          }
          // Otherwise continue with whatever results we have
        }
        
        // Merge API results (avoid duplicates)
        for (const asset of apiResults) {
          const symbolKey = `${asset.symbol.toUpperCase()}-${asset.type}`;
          if (!seenSymbols.has(symbolKey)) {
            seenSymbols.add(symbolKey);
            allResults.push(asset);
          }
        }
        
        // Merge AKTools results (avoid duplicates)
        for (const asset of aktoolsResults) {
          const symbolKey = `${asset.symbol.toUpperCase()}-${asset.type}`;
          if (!seenSymbols.has(symbolKey)) {
            seenSymbols.add(symbolKey);
            allResults.push(asset);
          }
        }
      }

      // Step 4.6: On-demand dictionary refresh (deferred, non-blocking)
      // If we have results, return immediately and refresh in background
      // If we have no results, still return immediately and refresh in background
      // This prevents blocking the search response
      const hasHan = /[\u4e00-\u9fa5]/.test(trimmedQuery);
      const looksLikeWord = /[a-z]/i.test(trimmedQuery) || hasHan;
      const onlyAliases = allResults.length > 0 && allResults.every(r => 
        (r.symbol === '0700.HK' || r.symbol === '1810.HK' || r.symbol === '600519.SH') && r.type === 'stock'
      );
      
      // Schedule dictionary refresh in background (non-blocking)
      if (looksLikeWord && (!type || type === 'stock') && !(window as any).__dictRefreshInProgress) {
        // Use setTimeout to defer execution and not block search return
        setTimeout(async () => {
          try {
            const { meta } = await assetDictionaryCache.loadCache();
            const cacheEmpty = !meta || (meta.counts.hk === 0 && meta.counts.a === 0);
            const cacheStale = assetDictionaryCache.shouldRefresh(meta, 24 * 60 * 60 * 1000);
            
            // Only refresh if cache is empty or stale
            if ((cacheEmpty || cacheStale) && !(window as any).__dictRefreshInProgress) {
              console.log(`[AssetDict] Background refresh triggered (empty=${cacheEmpty} stale=${cacheStale}) for query: ${trimmedQuery}`);
              (window as any).__dictRefreshInProgress = true;
              
              try {
                const { assets: refreshedAssets } = await assetDictionaryCache.refreshFromAktoolsAndPersist();
                await assetDataService.mergeAndUpdateAssets(refreshedAssets);
                console.log(`[AssetDict] Background refresh completed: ${refreshedAssets.length} assets`);
              } catch (error) {
                console.warn('[AssetDict] Background refresh failed:', error);
              } finally {
                (window as any).__dictRefreshInProgress = false;
              }
            }
          } catch (error) {
            console.warn('[AssetDict] Background refresh check failed:', error);
          }
        }, 0); // Defer to next event loop tick
      }
      
      // Step 5: If still no results, try fallback validation strategies
      if (allResults.length === 0) {
        // Strategy 1: Check if query looks like a symbol with exchange suffix (e.g., 0700.HK, 600519.SH)
        if (/^[\dA-Z]+\.[A-Z]{2}$/i.test(trimmedQuery)) {
          const symbol = trimmedQuery.toUpperCase();
          const exchangeMatch = symbol.match(/\.([A-Z]{2})$/);
          const exchange = exchangeMatch ? exchangeMatch[1] : undefined;
          
          // Determine asset type if not provided
          let assetType: AssetType | undefined = type;
          if (!assetType) {
            // HK and SH stocks are typically stock type
            if (exchange === 'HK' || exchange === 'SH') {
              // Check if it's an index (typically starts with 000001 for SH indices)
              if (exchange === 'SH' && symbol.startsWith('000001')) {
                assetType = 'index';
              } else {
                assetType = 'stock';
              }
            }
          }
          
          if (assetType) {
            try {
              // Try to validate the symbol
              const validationAsset: Partial<Asset> = { symbol, type: assetType };
              if (exchange) {
                validationAsset.exchange = exchange;
              }
              const validation = await this.validateAsset(
                validationAsset,
                { ...config, timeout: 5000 }
              );
              
              if (validation.valid && validation.asset) {
                const validatedResult: AssetSearchResult[] = [{
                  id: validation.asset.id || symbol.toLowerCase().replace(/\./g, '_'),
                  symbol: validation.asset.symbol || symbol,
                  name: validation.asset.name || symbol,
                  type: validation.asset.type || assetType,
                  ...(exchange && { exchange })
                }];
                
                // Merge into allResults
                allResults.push(...validatedResult);
              }
            } catch (error) {
              // If validation fails, continue with other strategies
            }
          }
        }
        
        // Strategy 2: Check if query looks like a valid asset symbol (stock, crypto, index, forex)
        if (allResults.length === 0) {
          const symbolPattern = this.detectSymbolPattern(trimmedQuery);
          if (symbolPattern) {
            // If user specified a type, use it; otherwise use detected type
            const assetTypeToTry = type || symbolPattern.type;
            
            // If pattern has multiple possible types (e.g., currency codes), try all of them
            const typesToTry: AssetType[] = symbolPattern.possibleTypes 
              ? (type ? [type, ...symbolPattern.possibleTypes.filter(t => t !== type)] : symbolPattern.possibleTypes)
              : (type && type !== symbolPattern.type 
                  ? [type, symbolPattern.type] 
                  : [assetTypeToTry]);
            
            const validatedResults: AssetSearchResult[] = [];
            
            for (const tryType of typesToTry) {
              try {
                const validation = await this.validateAsset(
                  { symbol: trimmedQuery.toUpperCase(), type: tryType },
                  { ...config, timeout: 5000 }
                );
                
                if (validation.valid && validation.asset) {
                  validatedResults.push({
                    id: validation.asset.id || trimmedQuery.toLowerCase(),
                    symbol: validation.asset.symbol || trimmedQuery.toUpperCase(),
                    name: validation.asset.name || trimmedQuery.toUpperCase(),
                    type: validation.asset.type || tryType
                  });
                }
              } catch (error) {
                // Continue to next type if validation fails
                continue;
              }
            }
            
            // Merge into allResults
            if (validatedResults.length > 0) {
              allResults.push(...validatedResults);
            }
          }
        }
      }
      
      // Step 6: Sort and deduplicate final results
      // Built-in results already have relevance sorting, API results are appended
      // Final deduplication by symbol+type
      // Also filter: name or symbol must fully contain the search query (case-insensitive)
      const finalResults: AssetSearchResult[] = [];
      const finalSeenSymbols = new Set<string>();
      const upperQuery = trimmedQuery.toUpperCase();
      const lowerQuery = trimmedQuery.toLowerCase();
      
      for (const asset of allResults) {
        const symbolKey = `${asset.symbol.toUpperCase()}-${asset.type}`;
        if (!finalSeenSymbols.has(symbolKey)) {
          // Filter: name or symbol must fully contain the search query (case-insensitive)
          const upperSymbol = (asset.symbol || '').toUpperCase();
          const upperName = (asset.name || '').toUpperCase();
          const lowerSymbol = (asset.symbol || '').toLowerCase();
          const lowerName = (asset.name || '').toLowerCase();
          
          // Check exact matches first (highest priority)
          const exactSymbolMatch = lowerSymbol === lowerQuery || upperSymbol === upperQuery;
          const exactNameMatch = lowerName === lowerQuery || upperName === upperQuery;
          
          // Check if symbol or name contains the query as a whole substring
          const containsInSymbol = upperSymbol.includes(upperQuery);
          const containsInName = upperName.includes(upperQuery);
          
          // For short queries (3 characters or less), require more strict matching
          // This prevents "zec" from matching "nzd" or "guanze"
          const isShortQuery = upperQuery.length <= 3;
          let isValidMatch = false;
          
          if (exactSymbolMatch || exactNameMatch) {
            // Exact matches are always valid
            isValidMatch = true;
          } else if (isShortQuery) {
            // For short queries, require:
            // 1. Symbol/name starts with query, OR
            // 2. Symbol/name contains query as a complete word (word boundary match)
            const symbolStartsWith = upperSymbol.startsWith(upperQuery) || lowerSymbol.startsWith(lowerQuery);
            const nameStartsWith = upperName.startsWith(upperQuery) || lowerName.startsWith(lowerQuery);
            
            // Word boundary match: check if query appears as a complete word
            const symbolWords = upperSymbol.split(/[\s\/\-_\.]+/);
            const nameWords = upperName.split(/[\s\/\-_\.]+/);
            const symbolWordMatch = symbolWords.some(word => word === upperQuery || word.startsWith(upperQuery));
            const nameWordMatch = nameWords.some(word => word === upperQuery || word.startsWith(upperQuery));
            
            isValidMatch = symbolStartsWith || nameStartsWith || symbolWordMatch || nameWordMatch;
          } else {
            // For longer queries, allow substring matching
            isValidMatch = containsInSymbol || containsInName;
          }
          
          if (isValidMatch) {
            finalSeenSymbols.add(symbolKey);
            finalResults.push(asset);
          }
        }
      }
      
      // Step 7: Reclassify obvious bond names (defensive labeling)
      const isBondName = (name: string): boolean => {
        const n = String(name || '').trim();
        if (!n) return false;
        const upper = n.toUpperCase();
        const keywords = [
          '债', '债券', '债务', '中期票据', '公司债', '企业债', '国债', '可转债', '可转换债',
          '金融债', '次级债', '永续债', '超短期融资券', '短期融资券', '定向工具', '资产支持证券',
          'MTN', 'BOND', 'ABS', 'MBS'
        ];
        if (keywords.some((keyword) => (keyword.length === 1 ? n.includes(keyword) : upper.includes(keyword)))) {
          return true;
        }
        return /\bBT\b/.test(upper);
      };

      for (let i = 0; i < finalResults.length; i++) {
        const item = finalResults[i];
        if (item.type !== 'bond' && isBondName(item.name)) {
          finalResults[i] = {
            ...item,
            id: `bond-${item.symbol.toLowerCase()}`,
            type: 'bond'
          };
        }
      }

      // Step 8: Sort results by type priority and relevance
      // Type priority: stock/index/forex/crypto/futures/etf/fund (high) > bond (low)
      // Within same type, sort by relevance (higher relevance first)
      const typePriority: Record<AssetType, number> = {
        stock: 1,
        index: 1,
        forex: 1,
        crypto: 1,
        futures: 1,
        etf: 1,
        fund: 1,
        bond: 2, // Lowest priority
      };
      
      // Import calculateRelevance from assetDataService (or define locally)
      const calculateRelevance = (query: string, asset: AssetSearchResult): number => {
        const lowerQuery = query.toLowerCase().trim();
        const lowerSymbol = asset.symbol.toLowerCase();
        const lowerName = asset.name.toLowerCase();
        
        // Exact symbol match: 100 points
        if (lowerSymbol === lowerQuery) return 100;
        
        // Symbol starts with query: 80 points
        if (lowerSymbol.startsWith(lowerQuery)) return 80;
        
        // Symbol contains query: 60 points
        if (lowerSymbol.includes(lowerQuery)) return 60;
        
        // Exact name match: 50 points
        if (lowerName === lowerQuery) return 50;
        
        // Name starts with query: 40 points
        if (lowerName.startsWith(lowerQuery)) return 40;
        
        // Name contains query: 30 points
        if (lowerName.includes(lowerQuery)) return 30;
        
        // Word boundary match in name: 20 points
        const nameWords = lowerName.split(/\s+/);
        if (nameWords.some(word => word.startsWith(lowerQuery))) return 20;
        
        return 0;
      };
      
      // Helper function to extract numeric code from symbol for proper sorting
      // For HK stocks: "00700.HK" -> 700, "0700.HK" -> 700, "05093.HK" -> 5093
      // For A-shares: "600519.SH" -> 600519
      const extractNumericCode = (symbol: string): number | null => {
        const upperSymbol = symbol.toUpperCase();
        
        // HK stock: extract 1-5 digit code before .HK (supports both "0700.HK" and "00700.HK")
        const hkMatch = upperSymbol.match(/^(\d{1,5})\.HK$/);
        if (hkMatch && hkMatch[1]) {
          return parseInt(hkMatch[1], 10);
        }
        
        // A-share: extract 6-digit code before .SH/.SZ/.BJ
        const aMatch = upperSymbol.match(/^(\d{6})\.(SH|SZ|BJ)$/);
        if (aMatch && aMatch[1]) {
          return parseInt(aMatch[1], 10);
        }
        
        return null;
      };
      
      finalResults.sort((a, b) => {
        // First sort by type priority
        const priorityA = typePriority[a.type] || 1;
        const priorityB = typePriority[b.type] || 1;
        if (priorityA !== priorityB) {
          return priorityA - priorityB; // Lower number = higher priority
        }
        
        // Then sort by relevance (higher relevance first)
        const relevanceA = calculateRelevance(trimmedQuery, a);
        const relevanceB = calculateRelevance(trimmedQuery, b);
        if (relevanceB !== relevanceA) {
          return relevanceB - relevanceA; // Descending order
        }
        
        // If same priority and relevance, sort by symbol (ascending)
        // For HK/A-share stocks, sort by numeric code; otherwise use string comparison
        const symbolA = a.symbol.toLowerCase();
        const symbolB = b.symbol.toLowerCase();
        
        // Check if both are HK or A-share stocks
        const numericCodeA = extractNumericCode(a.symbol);
        const numericCodeB = extractNumericCode(b.symbol);
        
        if (numericCodeA !== null && numericCodeB !== null) {
          // Both are HK or A-share stocks, sort by numeric code
          if (numericCodeA !== numericCodeB) {
            return numericCodeA - numericCodeB;
          }
          // If numeric codes are equal, fall back to string comparison
          return symbolA.localeCompare(symbolB);
        } else {
          // At least one is not HK/A-share, use string comparison
          if (symbolA !== symbolB) {
            return symbolA.localeCompare(symbolB);
          }
        }
        
        // If everything is the same, maintain original order (stable sort)
        return 0;
      });
      
      // Cache the result (only cache API results, built-in assets are always available)
      if (apiResults.length > 0 || finalResults.length > 0) {
        apiCache.set(cacheKey, finalResults, CacheConfig.SEARCH_RESULTS);
      }
      
      return finalResults;
    });
  }
  
  /**
   * Validate asset with caching
   */
  async validateAsset(
    asset: Partial<Asset>,
    config?: RequestConfig
  ): Promise<AssetValidationResult> {
    const cacheKey = CacheKeyGenerator.assetValidation(
      asset.symbol || '',
      asset.type || 'crypto'
    );
    
    // Check cache first
    const cached = apiCache.get<AssetValidationResult>(cacheKey);
    if (cached && !this.isCacheStale(cached, CacheConfig.ASSET_VALIDATION)) {
      return cached.data;
    }
    
    return this.deduplicator.deduplicate(cacheKey, async () => {
      // Filter providers based on exchange type
      let providers = this.getHealthyProviders(asset.type);
      
      const isChinese = this.isChineseStockAsset(asset);
      if (asset.type === 'stock') {
        if (isChinese) {
          // For Chinese stock exchanges (HK, SH, SZ, BJ), prioritize aktools provider
          const aktoolsProvider = providers.find(p => p.name === 'aktools');
          if (aktoolsProvider) {
            // Use aktools first, then other providers as fallback
            providers = [aktoolsProvider, ...providers.filter(p => p.name !== 'aktools')];
          }
        }
      }
      
      if (providers.length === 0) {
        return {
          valid: false,
          error: 'No healthy providers available for this asset type'
        };
      }
      
      let lastError: any;
      let lastResult: AssetValidationResult | null = null;
      
      // 尝试每个 provider，直到找到一个有效的验证结果
      for (const provider of providers) {
        try {
          // Respect cooldown for providers (e.g., after 429)
          const nextAvailableTime = this.providerNextAvailableTimes.get(provider.name);
          if (nextAvailableTime && Date.now() < nextAvailableTime) {
            continue;
          }

          await this.checkRateLimit(provider.name);
          const result = await provider.validateAsset(asset, config);
          
          // 如果验证成功，直接返回
          if (result.valid) {
            // Defensive: treat missing/invalid/zero price as invalid validation
            // Some providers return 0 for unknown symbols instead of throwing.
            const price = result.asset?.price;
            if (price === undefined || price === null || !isFinite(price) || price <= 0) {
              lastResult = {
                valid: false,
                error: `Invalid price data for ${asset.symbol || ''}`
              };
              continue;
            }

            // Cache the result
            apiCache.set(cacheKey, result, CacheConfig.ASSET_VALIDATION);
            return result;
          }
          
          // 如果验证失败，检查错误类型
          // 如果是速率限制错误或其他可重试的错误，继续尝试下一个 provider
          const errorMessage = result.error || '';
          const isRateLimitError = errorMessage.includes('API credits') || 
                                   errorMessage.toLowerCase().includes('rate limit') ||
                                   errorMessage.includes('RATE_LIMIT') ||
                                   errorMessage.includes('status code 429') ||
                                   /\b429\b/.test(errorMessage);
          
          if (isRateLimitError) {
            // 速率限制错误，继续尝试下一个 provider
            console.warn(`Provider ${provider.name} rate limited, trying next provider`);
            lastResult = result;
            continue;
          }
          
          // 其他错误，也继续尝试下一个 provider（可能是临时错误）
          lastResult = result;
          continue;
        } catch (error) {
          lastError = error;
          
          const err: any = error;
          if (err && typeof err === 'object') {
            if (err.code === 'RATE_LIMIT_ERROR') {
              const retryAfter = typeof err.retryAfter === 'number' ? err.retryAfter : 60000;
              this.providerNextAvailableTimes.set(provider.name, Date.now() + retryAfter);
            }
            if (err.code === 'AUTH_ERROR') {
              this.providerNextAvailableTimes.set(provider.name, Date.now() + 5 * 60 * 1000);
              providerRegistry.markProviderUnhealthy(provider.name, error instanceof Error ? error : undefined);
            }
          }

          // Convert thrown errors into an invalid result to avoid bubbling exceptions to UI
          lastResult = {
            valid: false,
            error: error instanceof Error ? error.message : String(error)
          };

          console.warn(`Provider ${provider.name} validation failed, trying next provider:`, error);
          // 继续尝试下一个 provider
          continue;
        }
      }
      
      // 如果所有 provider 都失败了，返回最后一个结果或错误
      if (lastResult) {
        // Cache the result even if it's invalid (to avoid repeated failures)
        apiCache.set(cacheKey, lastResult, CacheConfig.ASSET_VALIDATION);
        return lastResult;
      }
      
      // 如果没有结果，返回最后一个错误（避免抛出导致 UI 直接报错）
      const fallback: AssetValidationResult = {
        valid: false,
        error: lastError instanceof Error ? lastError.message : (lastError ? String(lastError) : 'All providers failed to validate asset')
      };
      apiCache.set(cacheKey, fallback, CacheConfig.ASSET_VALIDATION);
      return fallback;
    });
  }
  
  /**
   * Test API key
   */
  async testApiKey(
    provider: string,
    key: string,
    config?: RequestConfig
  ): Promise<ApiKeyTestResult> {
    const keyHash = this.hashApiKey(key);
    const cacheKey = CacheKeyGenerator.apiTest(provider, keyHash);
    
    // Check cache first (short TTL for API tests)
    const cached = apiCache.get<ApiKeyTestResult>(cacheKey);
    if (cached && !this.isCacheStale(cached, CacheConfig.API_TEST)) {
      return cached.data;
    }
    
    const apiProvider = providerRegistry.getProvider(provider);
    if (!apiProvider) {
      throw createApiError('VALIDATION_ERROR', `Unknown provider: ${provider}`);
    }
    
    try {
      const result = await this.retryManager.withRetry(
        async () => {
          await this.checkRateLimit(provider);
          return apiProvider.testConnection(config);
        },
        { maxRetries: 1 } // Don't retry API key tests
      );
      
      const testResult: ApiKeyTestResult = {
        valid: result,
        message: result ? 'API key is valid' : 'API key is invalid',
        provider
      };
      
      // Cache the result
      apiCache.set(cacheKey, testResult, CacheConfig.API_TEST);
      
      return testResult;
    } catch (error) {
      const testResult: ApiKeyTestResult = {
        valid: false,
        message: error instanceof Error ? error.message : 'API key test failed',
        provider
      };
      
      return testResult;
    }
  }
  
  /**
   * Set API key for provider
   */
  setApiKey(provider: string, key: string): void {
    // This would be implemented by individual providers
    console.log(`Setting API key for provider: ${provider}`);
  }
  
  /**
   * Get all providers
   */
  getProviders(): ApiProvider[] {
    return providerRegistry.getAllProviders();
  }
  
  /**
   * Get healthy providers for asset type
   */
  getHealthyProviders(assetType?: AssetType): ApiProvider[] {
    return providerRegistry.getHealthyProviders(assetType);
  }
  
  /**
   * Reset provider health status
   */
  resetProviderHealth(provider?: string): void {
    providerRegistry.resetProviderHealth(provider);
  }
  
  /**
   * Cancel requests by context
   */
  cancelRequests(context?: string): void {
    if (context) {
      const controller = this.abortControllers.get(context);
      if (controller) {
        controller.abort();
        this.abortControllers.delete(context);
        
        // Mark request as cancelled in context tracking
        const requestContext = this.requestContexts.get(context);
        if (requestContext) {
          requestContext.cancelled = true;
          this.logApiUsage(
            requestContext.operation,
            requestContext.provider || 'unknown',
            Date.now() - requestContext.startTime,
            false,
            new Error('Request cancelled')
          );
        }
        
        console.log(`Cancelled requests for context: ${context}`);
      }
    } else {
      // Cancel all requests
      for (const [ctx, controller] of this.abortControllers.entries()) {
        controller.abort();
        
        // Mark all requests as cancelled
        const requestContext = this.requestContexts.get(ctx);
        if (requestContext) {
          requestContext.cancelled = true;
          this.logApiUsage(
            requestContext.operation,
            requestContext.provider || 'unknown',
            Date.now() - requestContext.startTime,
            false,
            new Error('Request cancelled - bulk cancellation')
          );
        }
        
        console.log(`Cancelled requests for context: ${ctx}`);
      }
      this.abortControllers.clear();
    }
    
    // Clear pending deduplicated requests
    this.deduplicator.clear();
  }
  
  /**
   * Cancel requests for component unmounting
   * This is specifically designed for React component cleanup
   */
  cancelRequestsForComponent(componentId: string): void {
    const contextsToCancel = Array.from(this.abortControllers.keys())
      .filter(context => context.startsWith(`${componentId}-`));
    
    for (const context of contextsToCancel) {
      this.cancelRequests(context);
    }
    
    console.log(`Cancelled ${contextsToCancel.length} requests for component: ${componentId}`);
  }
  
  /**
   * Create a request context for tracking and cancellation
   */
  createRequestContext(operation: string, componentId?: string): string {
    const timestamp = Date.now();
    const context = componentId 
      ? `${componentId}-${operation}-${timestamp}`
      : `${operation}-${timestamp}`;
    
    // Track request context
    this.requestContexts.set(context, {
      startTime: timestamp,
      operation,
      cancelled: false
    });
    
    return context;
  }
  
  /**
   * Get request statistics
   */
  getRequestStats(): RequestStats {
    return { ...this.stats };
  }
  
  /**
   * Get API usage statistics for monitoring
   */
  getApiUsageStats(): {
    totalRequests: number;
    requestsPerProvider: Record<string, number>;
    errorRates: Record<string, number>;
    averageResponseTime: number;
    cacheHitRate: number;
    activeRequests: number;
  } {
    const totalErrors = Object.values(this.stats.errorsByType).reduce((sum, count) => sum + count, 0);
    const errorRate = this.stats.totalRequests > 0 ? totalErrors / this.stats.totalRequests : 0;
    
    // Calculate error rates per provider
    const errorRates: Record<string, number> = {};
    for (const [provider, requestCount] of Object.entries(this.stats.requestsByProvider)) {
      // This is a simplified calculation - in a real implementation, 
      // we'd track errors per provider separately
      errorRates[provider] = errorRate;
    }
    
    return {
      totalRequests: this.stats.totalRequests,
      requestsPerProvider: { ...this.stats.requestsByProvider },
      errorRates,
      averageResponseTime: this.stats.averageResponseTime,
      cacheHitRate: 0, // Would need to track cache hits/misses separately
      activeRequests: this.abortControllers.size
    };
  }
  
  /**
   * Log API usage for monitoring and debugging
   */
  logApiUsage(operation: string, provider: string, duration: number, success: boolean, error?: Error): void {
    const logEntry = {
      timestamp: new Date().toISOString(),
      operation,
      provider,
      duration,
      success,
      error: error ? {
        message: error.message,
        code: (error as any).code,
        stack: process.env.NODE_ENV === 'development' ? error.stack : undefined
      } : undefined,
      // Additional metadata for better monitoring
      metadata: {
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Node.js',
        timestamp_ms: Date.now(),
        session_id: this.getSessionId()
      }
    };
    
    // In a real implementation, this would send to a logging service
    console.log('API Usage:', JSON.stringify(logEntry));
    
    // Store recent logs for debugging (keep last 100)
    if (!this.recentLogs) {
      this.recentLogs = [];
    }
    this.recentLogs.push(logEntry);
    if (this.recentLogs.length > 100) {
      this.recentLogs.shift();
    }
    
    // Emit usage event for external monitoring systems
    this.emitUsageEvent(logEntry);
  }
  
  /**
   * Enhanced API usage logging with request lifecycle tracking
   */
  logRequestLifecycle(context: string, phase: 'start' | 'success' | 'error' | 'cancelled', data?: any): void {
    const requestContext = this.requestContexts.get(context);
    if (!requestContext) return;
    
    const logEntry = {
      timestamp: new Date().toISOString(),
      context,
      phase,
      operation: requestContext.operation,
      provider: requestContext.provider,
      duration: Date.now() - requestContext.startTime,
      data,
      metadata: {
        session_id: this.getSessionId(),
        request_id: context
      }
    };
    
    console.log('Request Lifecycle:', JSON.stringify(logEntry));
    
    // Store in recent logs
    this.recentLogs.push(logEntry);
    if (this.recentLogs.length > 100) {
      this.recentLogs.shift();
    }
    
    // Clean up context on completion
    if (['success', 'error', 'cancelled'].includes(phase)) {
      this.requestContexts.delete(context);
    }
  }
  
  /**
   * Get recent API logs for debugging
   */
  getRecentLogs(limit: number = 50): Array<{
    timestamp: string;
    operation: string;
    provider: string;
    duration: number;
    success: boolean;
    error?: {
      message: string;
      code?: string;
      stack?: string;
    };
  }> {
    if (!this.recentLogs) {
      return [];
    }
    return this.recentLogs.slice(-limit);
  }
  
  /**
   * Create monitoring utilities for debugging and observability
   */
  createMonitoringUtils() {
    return {
      // Health check endpoint
      healthCheck: async (): Promise<{
        status: 'healthy' | 'degraded' | 'unhealthy';
        providers: Array<{
          name: string;
          healthy: boolean;
          lastError?: Date;
        }>;
        stats: RequestStats;
        activeRequests: number;
        requestContexts: number;
      }> => {
        const providers = this.getProviders();
        const healthyProviders = providers.filter(p => p.isHealthy);
        
        let status: 'healthy' | 'degraded' | 'unhealthy' = 'healthy';
        if (healthyProviders.length === 0) {
          status = 'unhealthy';
        } else if (healthyProviders.length < providers.length) {
          status = 'degraded';
        }
        
        return {
          status,
          providers: providers.map(p => ({
            name: p.name,
            healthy: p.isHealthy,
            ...(p.lastError && { lastError: p.lastError })
          })),
          stats: this.getRequestStats(),
          activeRequests: this.abortControllers.size,
          requestContexts: this.requestContexts.size
        };
      },
      
      // Performance metrics
      getPerformanceMetrics: () => {
        const stats = this.getRequestStats();
        const usageStats = this.getApiUsageStats();
        
        return {
          requestVolume: stats.totalRequests,
          successRate: stats.totalRequests > 0 ? stats.successfulRequests / stats.totalRequests : 0,
          errorRate: stats.totalRequests > 0 ? stats.failedRequests / stats.totalRequests : 0,
          averageResponseTime: stats.averageResponseTime,
          providerDistribution: usageStats.requestsPerProvider,
          activeRequests: usageStats.activeRequests,
          lastUpdated: stats.lastUpdated,
          // Enhanced metrics
          requestsPerMinute: this.calculateRequestsPerMinute(),
          errorsByProvider: this.getErrorsByProvider(),
          slowestRequests: this.getSlowestRequests(),
          cancelledRequests: this.getCancelledRequestsCount()
        };
      },
      
      // Debug information
      getDebugInfo: () => ({
        recentLogs: this.getRecentLogs(20),
        cacheStats: {
          // Would integrate with cache statistics if available
          size: 0,
          hitRate: 0
        },
        providerHealth: this.getProviders().map(p => ({
          name: p.name,
          healthy: p.isHealthy,
          priority: p.priority,
          supportedTypes: p.supportedAssetTypes,
          lastError: p.lastError
        })),
        activeContexts: Array.from(this.requestContexts.entries()).map(([context, data]) => ({
          context,
          operation: data.operation,
          provider: data.provider,
          duration: Date.now() - data.startTime,
          cancelled: data.cancelled
        })),
        systemInfo: {
          nodeVersion: typeof process !== 'undefined' ? process.version : 'browser',
          platform: typeof process !== 'undefined' ? process.platform : 'browser',
          memory: typeof process !== 'undefined' ? process.memoryUsage() : undefined
        }
      }),
      
      // Request tracking utilities
      getActiveRequests: () => {
        return Array.from(this.requestContexts.entries()).map(([context, data]) => ({
          context,
          operation: data.operation,
          provider: data.provider,
          startTime: new Date(data.startTime),
          duration: Date.now() - data.startTime,
          cancelled: data.cancelled
        }));
      },
      
      // Export logs for external analysis
      exportLogs: (format: 'json' | 'csv' = 'json') => {
        if (format === 'csv') {
          return this.exportLogsAsCSV();
        }
        return JSON.stringify(this.recentLogs, null, 2);
      },
      
      // Clear logs and reset statistics
      clearLogs: () => {
        this.recentLogs = [];
        console.log('Cleared API usage logs');
      },
      
      // Set up real-time monitoring
      setupRealTimeMonitoring: (callback: (event: any) => void) => {
        this.monitoringCallback = callback;
        console.log('Real-time monitoring enabled');
      }
    };
  }
  
  // Private helper methods
  
  private async executeWithFailover<T>(
    operation: (provider: ApiProvider) => Promise<T>,
    assetType?: AssetType,
    config?: RequestConfig,
    asset?: Partial<Asset>
  ): Promise<T> {
    let providers = this.getHealthyProviders(assetType);
    
    // Filter providers based on exchange type
    const isChinese = this.isChineseStockAsset(asset);
    if (assetType === 'stock') {
      if (isChinese) {
        // For Chinese stock exchanges (HK, SH, SZ, BJ), prioritize aktools provider
        const aktoolsProvider = providers.find(p => p.name === 'aktools');
        if (aktoolsProvider) {
          // Use aktools first, then other providers as fallback
          providers = [aktoolsProvider, ...providers.filter(p => p.name !== 'aktools')];
        }
      }
    }
    
    if (providers.length === 0) {
      throw createApiError(
        'SERVER_ERROR',
        'No healthy providers available for this asset type'
      );
    }
    
    let lastError: any;
    
    // 每个 provider 只调用 1 次，失败就换下一个
    for (const provider of providers) {
      const startTime = Date.now();
      // Check if provider is in cooldown period (20 seconds after failure)
      const nextAvailableTime = this.providerNextAvailableTimes.get(provider.name);
      if (nextAvailableTime && Date.now() < nextAvailableTime) {
        const remainingMs = nextAvailableTime - Date.now();
        const remainingCooldown = Math.ceil(remainingMs / 1000);
        console.log(`Provider ${provider.name} is in cooldown (${remainingCooldown}s remaining), skipping...`);
        continue;
      }
      
      // Rate limiting: ensure minimum interval between requests to same provider
      const lastRequestTime = this.lastRequestTimes.get(provider.name);
      if (lastRequestTime) {
        const timeSinceLastRequest = Date.now() - lastRequestTime;
        if (timeSinceLastRequest < this.MIN_REQUEST_INTERVAL_MS) {
          const delay = this.MIN_REQUEST_INTERVAL_MS - timeSinceLastRequest;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      
      try {
        // Update last request time
        this.lastRequestTimes.set(provider.name, Date.now());
        
        // 直接调用，不重试（每个 API 只调用 1 次）
        const result = await operation(provider);
        
        const duration = Date.now() - startTime;
        
        // Reset failure backoff on success
        this.providerNextAvailableTimes.delete(provider.name);
        this.providerFailureCounts.delete(provider.name);
        
        // Update statistics
        this.updateStats(provider.name, duration, true);
        
        // Log successful operation
        this.logApiUsage('api_request', provider.name, duration, true);
        
        return result;
      } catch (error) {
        lastError = error;
        const duration = Date.now() - startTime;
        
        const err: any = error;
        const isRateLimit = err && typeof err === 'object' && err.code === 'RATE_LIMIT_ERROR';
        const isAuthError = err && typeof err === 'object' && err.code === 'AUTH_ERROR';
        
        let delay: number;
        if (isAuthError) {
          delay = 5 * 60 * 1000;
        } else {
          const failureCount = (this.providerFailureCounts.get(provider.name) || 0) + 1;
          this.providerFailureCounts.set(provider.name, failureCount);
          const baseDelay = Math.min(
            this.FAILURE_COOLDOWN_MS * Math.pow(2, failureCount - 1),
            this.FAILURE_COOLDOWN_MAX_MS
          );
          const jitter = baseDelay * this.FAILURE_JITTER_RATIO * (Math.random() * 2 - 1);
          delay = Math.max(0, Math.round(baseDelay + jitter));
          const retryAfter = isRateLimit && typeof err.retryAfter === 'number' ? err.retryAfter : undefined;
          if (retryAfter && retryAfter > 0) {
            delay = Math.max(delay, retryAfter);
          }
        }
        
        this.providerNextAvailableTimes.set(provider.name, Date.now() + delay);
        
        if (this.isPersistentError(error)) {
          providerRegistry.markProviderUnhealthy(provider.name, error instanceof Error ? error : undefined);
        }
        
        // Update statistics
        this.updateStats(provider.name, duration, false, error);
        
        // Log failed operation
        this.logApiUsage('api_request', provider.name, duration, false, error instanceof Error ? error : new Error(String(error)));
        
        console.warn(`Provider ${provider.name} failed, will retry after ${Math.ceil(delay / 1000)}s cooldown. Trying next provider:`, error);
        // 继续尝试下一个 provider（不重试当前 provider）
        continue;
      }
    }
    
    // All providers failed
    throw lastError || createApiError('SERVER_ERROR', 'All providers failed');
  }
  
  private async checkRateLimit(provider: string): Promise<void> {
    const allowed = await this.rateLimiter.checkLimit(provider);
    if (!allowed) {
      throw createApiError(
        'RATE_LIMIT_ERROR',
        `Rate limit exceeded for provider: ${provider}`,
        { retryAfter: 60000 }
      );
    }
  }
  
  private groupAssetsByType(assets: Asset[]): Map<AssetType, Asset[]> {
    const groups = new Map<AssetType, Asset[]>();
    
    for (const asset of assets) {
      const existing = groups.get(asset.type) || [];
      existing.push(asset);
      groups.set(asset.type, existing);
    }
    
    return groups;
  }
  
  private isCacheStale(cached: any, maxAge: number): boolean {
    const age = Date.now() - cached.timestamp.getTime();
    return age > maxAge;
  }
  
  /**
   * Detect if a query looks like a valid asset symbol
   * Returns the likely asset type or null if it doesn't match any pattern
   * For ambiguous symbols (like currency codes), returns multiple possible types
   */
  private detectSymbolPattern(query: string): { type: AssetType; pattern: string; possibleTypes?: AssetType[] } | null {
    const trimmed = query.trim().toUpperCase();
    
    // Common currency codes that could be forex or crypto (stablecoins)
    const currencyCodes = ['USD', 'EUR', 'GBP', 'JPY', 'CNY', 'CHF', 'AUD', 'CAD', 'NZD', 'SGD', 'HKD', 'KRW', 'INR', 'BRL', 'MXN', 'RUB', 'ZAR', 'TRY', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF'];
    const isCurrencyCode = currencyCodes.includes(trimmed);
    
    // Forex pattern: contains "/" or "_" (e.g., USD/CNY, EUR_USD)
    const forexPattern = /^[A-Z]{3}\/[A-Z]{3}$|^[A-Z]{3}_[A-Z]{3}$/;
    if (forexPattern.test(trimmed)) {
      return { type: 'forex', pattern: 'forex' };
    }
    
    // If it's a currency code, prioritize forex and crypto (for stablecoins)
    if (isCurrencyCode) {
      return { type: 'forex', pattern: 'currency_code', possibleTypes: ['forex', 'crypto'] };
    }
    
    // Stock pattern: 3-5 uppercase letters (e.g., NVDA, APPL, GOOG)
    const stockPattern = /^[A-Z]{3,5}$/;
    if (stockPattern.test(trimmed)) {
      return { type: 'stock', pattern: 'stock' };
    }
    
    // Crypto pattern: 2-5 uppercase letters (e.g., BTC, ETH, SOL)
    const cryptoPattern = /^[A-Z]{2,5}$/;
    if (cryptoPattern.test(trimmed)) {
      return { type: 'crypto', pattern: 'crypto' };
    }
    
    // Index pattern: 3-5 uppercase letters or numbers (e.g., SPX, IXIC, DJI)
    const indexPattern = /^[A-Z0-9]{3,5}$/;
    if (indexPattern.test(trimmed)) {
      return { type: 'index', pattern: 'index' };
    }
    
    return null;
  }
  
  private isPersistentError(error: any): boolean {
    const persistentErrors = ['AUTH_ERROR', 'SERVER_ERROR'];
    return persistentErrors.includes(error.code);
  }
  
  private updateStats(
    provider: string,
    responseTime: number,
    success: boolean,
    error?: any
  ): void {
    this.stats.totalRequests++;
    
    if (success) {
      this.stats.successfulRequests++;
      
      // Update average response time
      const totalTime = this.stats.averageResponseTime * (this.stats.successfulRequests - 1);
      this.stats.averageResponseTime = (totalTime + responseTime) / this.stats.successfulRequests;
    } else {
      this.stats.failedRequests++;
      
      if (error?.code && this.stats.errorsByType.hasOwnProperty(error.code)) {
        this.stats.errorsByType[error.code as ApiErrorCode] = (this.stats.errorsByType[error.code as ApiErrorCode] || 0) + 1;
      }
    }
    
    // Update provider stats
    this.stats.requestsByProvider[provider] = (this.stats.requestsByProvider[provider] || 0) + 1;
    this.stats.lastUpdated = new Date();
  }
  
  private hashApiKey(key: string): string {
    // Simple hash for caching (not for security)
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      const char = key.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
  }
  
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  private getSessionId(): string {
    return this.sessionId;
  }
  
  private emitUsageEvent(logEntry: any): void {
    if (this.monitoringCallback) {
      this.monitoringCallback({
        type: 'api_usage',
        data: logEntry
      });
    }
  }
  
  private calculateRequestsPerMinute(): number {
    const oneMinuteAgo = Date.now() - 60000;
    const recentRequests = this.recentLogs.filter(log => 
      new Date(log.timestamp).getTime() > oneMinuteAgo
    );
    return recentRequests.length;
  }
  
  private getErrorsByProvider(): Record<string, number> {
    const errorsByProvider: Record<string, number> = {};
    
    this.recentLogs.forEach(log => {
      if (!log.success && log.provider) {
        errorsByProvider[log.provider] = (errorsByProvider[log.provider] || 0) + 1;
      }
    });
    
    return errorsByProvider;
  }
  
  private getSlowestRequests(): Array<{
    operation: string;
    provider: string;
    duration: number;
    timestamp: string;
  }> {
    return this.recentLogs
      .filter(log => log.duration && log.success)
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 10)
      .map(log => ({
        operation: log.operation,
        provider: log.provider,
        duration: log.duration,
        timestamp: log.timestamp
      }));
  }
  
  private getCancelledRequestsCount(): number {
    return this.recentLogs.filter(log => 
      log.error && log.error.message && log.error.message.includes('cancelled')
    ).length;
  }
  
  private exportLogsAsCSV(): string {
    if (this.recentLogs.length === 0) {
      return 'timestamp,operation,provider,duration,success,error_message\n';
    }
    
    const headers = 'timestamp,operation,provider,duration,success,error_message\n';
    const rows = this.recentLogs.map(log => {
      const errorMessage = log.error ? log.error.message.replace(/,/g, ';') : '';
      return `${log.timestamp},${log.operation},${log.provider},${log.duration},${log.success},"${errorMessage}"`;
    }).join('\n');
    
    return headers + rows;
  }

  /**
   * Detect whether a stock asset belongs to Chinese markets.
   * Covers explicit exchange/suffix and common bare numeric symbols.
   */
  private isChineseStockAsset(asset?: Partial<Asset>): boolean {
    if (!asset || asset.type !== 'stock') return false;

    const exchange = String(asset.exchange || '').trim().toUpperCase();
    const symbolUpper = String(asset.symbol || '').trim().toUpperCase();
    if (!symbolUpper) return false;

    // 1) Explicit exchange hint
    if (['HK', 'SH', 'SZ', 'BJ', 'SS'].includes(exchange)) {
      return true;
    }

    // 2) Explicit suffix hint
    if (/\.(HK|SH|SZ|BJ|SS)$/.test(symbolUpper)) {
      return true;
    }

    // 3) Prefixed mainland code, e.g. SH600519 / SZ000001
    if (/^(SH|SZ|BJ)\d{6}$/.test(symbolUpper)) {
      return true;
    }

    // 4) Bare HK code: 1-5 digits (commonly 4-5 in user input)
    if (/^\d{1,5}$/.test(symbolUpper)) {
      return true;
    }

    // 5) Bare A-share/BJ code: 6 digits
    if (/^(6|0|3|4|8|9)\d{5}$/.test(symbolUpper)) {
      return true;
    }

    return false;
  }
}

// Export singleton instance
export const unifiedApiService = new UnifiedApiService();